import { useCallback, useMemo } from 'react';
import { useEditorContext } from '../editor/useEditorContext';
import type { NodeDisplayState } from '../editor/EditorContext';
import type { NodeRunGateState } from '../editor/runStatus/upstreamGating';
import type { ValidationError } from '../editor/validation/types';

/**
 * Per-node selector over the editor's run session.
 *
 * <p>The single SSE stream lives in {@code useProcessRunSession} (set up
 * once by the {@code ProcessEditorPage}). This hook is a thin selector
 * for components rendered <em>inside</em> the {@code EditorContext.Provider}
 * (today: {@code NodeHeaderRunButton}; future: per-node
 * inspector tabs, footer affordances) that want a focused per-node API
 * instead of pulling several fields out of context by hand.</p>
 *
 * <p>Returns merged status (live SSE state when this node is in flight,
 * persisted snapshot otherwise) plus the {@link NodeRunGateState} so the
 * caller can render an accurate "Run" / "Disabled because…" affordance
 * without computing the gate themselves.</p>
 *
 * <p>Why a selector and not a separate SSE owner: opening a second SSE
 * stream per node would double-count tool runs in Datadog, miss
 * cross-node events (gate decisions, full-run cascade), and let the
 * "single run at a time" guard slip. One stream, one owner, many
 * selectors.</p>
 */
export interface UseNodeRunReturn {
  /** Merged display state — never null. {@code source: 'none'} for fresh nodes. */
  readonly state: NodeDisplayState;
  /** Per-node gate descriptor. {@code canRun: true} enables the Run button. */
  readonly gate: NodeRunGateState;
  /** Convenience flag — true iff THIS node is currently running. */
  readonly isRunning: boolean;
  /**
   * Validation errors attached to this node — empty when the node is
   * healthy. Drives the {@code hasWarning} flag below + the tooltip the
   * run control renders when it morphs to the "Check" affordance.
   */
  readonly errors: readonly ValidationError[];
  /**
   * True when {@link errors} is non-empty. Renderers use this to flip the
   * card border to red and morph the run button into "Check" + a red
   * error glyph; the underlying {@link gate} reason may also be
   * `not_runnable` for the same root cause, but the warning surface is
   * driven by validation errors so the user gets actionable copy
   * regardless of which layer caught the issue.
   */
  readonly hasWarning: boolean;
  /** Start a single-node test run. No-op if {@link gate}.canRun is false. */
  readonly start: () => void;
  /** Cancel the active run if this node is the one in flight. No-op otherwise. */
  readonly cancel: () => void;
}

export function useNodeRun(nodeId: string): UseNodeRunReturn {
  const {
    runStateForNode,
    getRunGate,
    errorsForNode,
    startNodeRun,
    cancelNodeRun,
  } = useEditorContext();

  const state = runStateForNode(nodeId);
  const gate = getRunGate(nodeId);
  const errors = errorsForNode(nodeId);
  const isRunning = state.status === 'running';
  const hasWarning = errors.length > 0;

  const start = useCallback(() => {
    if (!gate.canRun) return;
    startNodeRun(nodeId);
  }, [gate.canRun, nodeId, startNodeRun]);

  const cancel = useCallback(() => {
    if (!isRunning) return;
    cancelNodeRun();
  }, [cancelNodeRun, isRunning]);

  return useMemo(
    () => ({ state, gate, errors, hasWarning, isRunning, start, cancel }),
    [state, gate, errors, hasWarning, isRunning, start, cancel],
  );
}
